import type { ContextHint, ContextHintOption } from "./api";
import { failedExpectations, normalizeAgreeValues } from "./context-checks";

export interface HintEvidence {
    hint: ContextHint;
    clause: string;
    reasoning?: string;
}
export interface AnswerHintGroup {
    key: string;
    label: string;
    positive: HintEvidence[];
    negative: HintEvidence[];
    additional: HintEvidence[];
    conflict: boolean;
    differs: boolean;
}

/** Group source evidence by the actual form option, without treating silence as denial. */
export function groupContextHints(
    hints: readonly ContextHint[],
    answer: string | undefined,
    options: readonly { value: string; text: string }[] = []
): AnswerHintGroup[] {
    const lookup = { options: options.map((o) => ({ value: o.value, label: o.text })) };
    // [] is an explicit deselection. An absent/blank answer has no comparison.
    const answered = answer != null && answer.trim() !== "" && answer !== "null";
    const groups = new Map<string, AnswerHintGroup>();
    const canonical = (v: string) => normalizeAgreeValues(lookup, [v])[0] ?? v.trim();
    const keyFor = (v: string) => canonical(v).toLowerCase();
    const get = (key: string, label: string) => {
        let group = groups.get(key);
        if (!group) {
            group = {
                key,
                label,
                positive: [],
                negative: [],
                additional: [],
                conflict: false,
                differs: false
            };
            groups.set(key, group);
        }
        return group;
    };
    const add = (list: HintEvidence[], hint: ContextHint, clause: string, reasoning?: string) => {
        if (
            !list.some(
                (e) =>
                    e.hint.sourceLabel === hint.sourceLabel &&
                    e.hint.observedAt === hint.observedAt &&
                    e.hint.itemIds.join() === hint.itemIds.join() &&
                    e.clause === clause
            )
        ) {
            list.push({ hint, clause, ...(reasoning ? { reasoning } : {}) });
        }
    };
    for (const hint of hints) {
        // Explicitly unknown answers are context, even if stale option metadata remains.
        if (hint.answer === null) {
            continue;
        }
        for (const option of hint.options ?? []) {
            const value = canonical(option.value);
            const group = get(
                keyFor(value),
                options.find((o) => o.value === value)?.text ?? option.label
            );
            add(
                option.present ? group.positive : group.negative,
                hint,
                option.clause,
                option.reasoning ?? hint.reasoning
            );
            const expected: ContextHintOption = { ...option, value };
            if (
                answered &&
                answer != null &&
                failedExpectations(answer, [expected], lookup).length > 0
            ) {
                group.differs = true;
            }
        }
    }
    for (const group of groups.values()) {
        group.conflict = group.positive.length > 0 && group.negative.length > 0;
    }
    for (const hint of hints) {
        if (hint.answer !== null && hint.options?.length) {
            continue;
        }
        // The server also emits a summary of a conflict. Its actual evidence
        // already appears under both answer states; don't repeat the summary.
        if (
            hint.answer !== null &&
            hint.conflict &&
            hint.conflict.values.every((v) => groups.get(keyFor(v))?.conflict)
        ) {
            continue;
        }
        const group = get("additional-context", "Additional context");
        add(group.additional, hint, hint.text, hint.reasoning);
        group.conflict ||= hint.answer !== null && hint.conflict != null;
    }
    // Negative-only evidence is useful only when it challenges a recorded
    // selection. Source disagreements retain both sides, even before answering.
    return [...groups.values()].filter(
        (group) =>
            group.positive.length > 0 ||
            group.additional.length > 0 ||
            group.differs ||
            group.conflict
    );
}
